import React, { useState, useEffect } from 'react';
import { useParams, Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import axios from 'axios';
import { ArrowLeft, Calendar } from 'lucide-react';
import SEO from '../components/SEO';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const BlogPost = () => {
  const { slug } = useParams();
  const [post, setPost] = useState(null);
  const [loading, setLoading] = useState(true);

 useEffect(() => {
  fetchPost();
  // eslint-disable-next-line react-hooks/exhaustive-deps
}, []);

  const fetchPost = async () => {
    try {
      const response = await axios.get(`${API}/blog/${slug}`);
      setPost(response.data);
    } catch (error) {
      console.error('Error fetching post:', error);
    } finally {
      setLoading(false);
    }
  };

  const formatDate = (dateString) => {
    const date = new Date(dateString);
    return date.toLocaleDateString('es-ES', { year: 'numeric', month: 'long', day: 'numeric' });
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center pt-32">
        <p className="text-lg font-light text-ink/60">Cargando artículo...</p>
      </div>
    );
  }

  if (!post) {
    return (
      <div className="min-h-screen flex flex-col items-center justify-center pt-32" data-testid="post-not-found">
        <p className="text-lg font-light text-ink/60 mb-8">Artículo no encontrado</p>
        <Link to="/blog" className="text-editorial hover:underline">
          Volver al Blog
        </Link>
      </div>
    );
  }

  return (
    <>
      <SEO title={`${post.title} | Blog`} description={post.excerpt} image={post.image} type="article" />

      <div className="pt-32 pb-24">
        {/* Back Button */}
        <section className="px-6 md:px-12 lg:px-24 mb-12">
          <div className="max-w-screen-2xl mx-auto">
            <Link
              to="/blog"
              data-testid="back-to-blog"
              className="inline-flex items-center gap-2 text-sm font-sans tracking-widest uppercase hover:text-editorial transition-colors"
            >
              <ArrowLeft className="w-4 h-4" />
              Volver al Blog
            </Link>
          </div>
        </section>

        {/* Post Header */}
        <section className="px-6 md:px-12 lg:px-24 mb-16" data-testid="post-header">
          <div className="max-w-3xl mx-auto text-center">
            <motion.div initial={{ opacity: 0, y: 30 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.8 }}>
              <div className="flex items-center justify-center gap-4 mb-6">
                <span className="text-sm font-sans tracking-widest uppercase text-editorial">{post.category}</span>
                <span className="flex items-center gap-2 text-sm font-sans tracking-widest uppercase text-ink/60">
                  <Calendar className="w-4 h-4" />
                  {formatDate(post.date)}
                </span>
              </div>
              <h1 className="text-4xl md:text-6xl font-bold tracking-tight" data-testid="post-title">
                {post.title}
              </h1>
            </motion.div>
          </div>
        </section>

        {/* Featured Image */}
        <section className="px-6 md:px-12 lg:px-24 mb-16">
          <div className="max-w-screen-xl mx-auto">
            <motion.div
              initial={{ opacity: 0, scale: 0.95 }}
              animate={{ opacity: 1, scale: 1 }}
              transition={{ duration: 0.8 }}
              className="editorial-border"
            >
              <img
                src={post.image}
                alt={post.title}
                className="w-full h-[500px] object-cover"
                data-testid="post-featured-image"
              />
            </motion.div>
          </div>
        </section>

        {/* Post Content */}
        <section className="px-6 md:px-12 lg:px-24">
          <div className="max-w-3xl mx-auto">
            <motion.div
              initial={{ opacity: 0, y: 30 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.3, duration: 0.8 }}
              className="prose prose-lg max-w-none"
            >
              <div
                className="text-lg font-light leading-relaxed text-ink/80 whitespace-pre-line"
                data-testid="post-content"
              >
                {post.content}
              </div>
            </motion.div>
          </div>
        </section>
      </div>
    </>
  );
};

export default BlogPost;
